import { Box, xcss } from '@atlaskit/primitives';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { useTrackDatabaseMetrics } from '@databases/analytics';
import { databaseState } from '@databases/app-access/state';
import { usePresenceSync } from '@databases/collab-state-manager/usePresenceSync';
import { useUnreadComments } from '@databases/comments/hooks';
import { useConnections } from '@databases/connections/hooks/useConnections';
import { useCopyListener } from '@databases/copy-paste/useCopyListener';
import { useCutListener } from '@databases/copy-paste/useCutListener';
import { useDatabaseContent } from '@databases/database-state';
import { setDatabaseContentReady } from '@databases/database-state/state/initial-database-loading-state';
import { Metrics } from '@databases/frontend-metrics';
import { useInvalidateMediaClientCacheForContentId } from '@databases/media-client';
import {
  getAppMode,
  isEmbed as isEmbedCheck,
  isNativeEmbedEditorPlugin as isNativeEmbedEditorPluginCheck
} from '@databases/modes';
import { appStoreUtils } from '@databases/redux/store';
import EmbedReference from '@databases/referencing/EmbedReference';
import { container } from '@databases/service-container';
import { selectDatabaseByContentIdStatus } from '@databases/utils/redux/databases/selectors';
import useView from '@databases/views/useView';
import React, { useCallback, useEffect, useMemo } from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags';

import DatabaseContentLoader from './DatabaseContentLoader';
import EmbedValueView from './apps/embed/components/EmbedValueView';
import EmbedEntryDetails from './apps/embed/components/entry/EmbedEntryDetails';
import NativeEmbedEditorPluginReferenceEntryDetails from './apps/embed/components/entry/NativeEmbedEditorPluginReferenceEntryDetails';
import { ValueResizeObserver } from './apps/embed/components/value-resize-observer';
import DatabaseLayoutRenderer from './common/components/database-content/DatabaseLayoutRenderer';
import Toolbar from './common/components/database-content/Toolbar';

const EmbedWrapper = ({
  children,
  isValueEmbed
}: {
  children: React.ReactNode;
  isValueEmbed?: boolean;
}) => {
  return (
    <Box
      testId="embed-wrapper"
      xcss={[
        isValueEmbed
          ? xcss({
              padding: 'space.0'
            })
          : xcss({
              display: 'flex',
              flexFlow: 'column',
              position: 'absolute',
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              paddingTop: 'space.0',
              paddingBottom: 'space.0',
              paddingRight: 'space.200',
              paddingLeft: 'space.200'
            })
      ]}
    >
      {children}
    </Box>
  );
};

const AppContent = () => {
  const { contentId, entryId, fieldId, referencing } = useDatabaseContent();
  const { view } = useView();

  const databaseStatus = appStoreUtils.useSelector((state) =>
    selectDatabaseByContentIdStatus(state, { contentId })
  );

  useConnections();

  const trackMetricsParams = useMemo(
    () => ({ databaseStatus, viewSettings: view.settings.layout }),
    [databaseStatus, view.settings.layout]
  );

  useTrackDatabaseMetrics(trackMetricsParams);

  useUnreadComments();

  useInvalidateMediaClientCacheForContentId(contentId);

  usePresenceSync(contentId);

  useCopyListener({ contentId });

  useCutListener({ contentId });

  const isEmbed = isEmbedCheck();
  const isNativeEmbedEditorPlugin = isNativeEmbedEditorPluginCheck();
  const shouldShowReferencingEmbed = isNativeEmbedEditorPlugin || isEmbed;

  useEffect(() => {
    databaseState.reactAppIsReady = true;
  }, []);

  useEffect(() => {
    if (shouldShowReferencingEmbed && referencing) {
      setDatabaseContentReady();
    }
  }, [referencing, shouldShowReferencingEmbed]);

  const sendDatabaseUIReadyCommand = useCallback(() => {
    setDatabaseContentReady();
    const eventBus = container.resolve('EventBus');
    eventBus.dispatch('databaseUILoaded', null);
  }, []);

  const isMauiEnabled = getExperimentValue('mauiExperiment', 'isEnabled', {
    fireExperimentExposure: false
  });
  const isDbNativeEmbedsV2Enabled =
    isMauiEnabled &&
    getExperimentValue('dbNativeEmbedsV2', 'isEnabled', {
      fireExperimentExposure: true
    });

  if (shouldShowReferencingEmbed && referencing) {
    GlobalPageLoadExperience.setPageLoadId(Metrics.EMBED_REFERENCE_LOAD);

    const EntryPreview = isDbNativeEmbedsV2Enabled
      ? NativeEmbedEditorPluginReferenceEntryDetails
      : EmbedEntryDetails;

    return (
      <EmbedWrapper>
        <EmbedReference
          ToolbarComponent={(props) => <Toolbar {...props} mode={getAppMode()} />}
          DatabasePreviewComponent={DatabaseLayoutRenderer}
          EntryPreviewComponent={EntryPreview}
          ValuePreviewComponent={EmbedValueView}
        />
      </EmbedWrapper>
    );
  }

  if (shouldShowReferencingEmbed && contentId && entryId && fieldId) {
    GlobalPageLoadExperience.setPageLoadId(Metrics.EMBED_VALUE_LOAD);
    return (
      <ValueResizeObserver>
        <EmbedWrapper isValueEmbed={true}>
          <EmbedValueView
            contentId={contentId}
            entryId={entryId}
            fieldId={fieldId}
            onDataLoaded={sendDatabaseUIReadyCommand}
          />
        </EmbedWrapper>
      </ValueResizeObserver>
    );
  }

  if (shouldShowReferencingEmbed && contentId && entryId) {
    GlobalPageLoadExperience.setPageLoadId(Metrics.EMBED_ENTRY_LOAD);

    if (isDbNativeEmbedsV2Enabled) {
      return (
        <EmbedWrapper>
          <NativeEmbedEditorPluginReferenceEntryDetails
            contentId={contentId}
            entryId={entryId}
            onDataLoaded={sendDatabaseUIReadyCommand}
          />
        </EmbedWrapper>
      );
    }

    return (
      <EmbedWrapper>
        <EmbedEntryDetails
          contentId={contentId}
          entryId={entryId}
          onDataLoaded={sendDatabaseUIReadyCommand}
        />
      </EmbedWrapper>
    );
  }

  GlobalPageLoadExperience.setPageLoadId(
    shouldShowReferencingEmbed
      ? Metrics.EMBED_DETAIL_LOAD
      : Metrics.STANDALONE_DETAIL_LOAD
  );

  return (
    <>
      {contentId ? (
        <DatabaseContentLoader onDataLoaded={sendDatabaseUIReadyCommand} />
      ) : null}
    </>
  );
};

export default AppContent;
